import { useAppNavigate } from '~/pages';
import { Plan, useSpringBigStore, useSubscribe } from '@springbig/shared';
import { useEffect, useState } from 'react';
import { BillingSchedule } from '~/pages/subscriptions/components/SubscribeForm';
import { CreditCardDto } from '~/shared/CreditCardForm/hooks/useCreditCardForm';
import { Destinations } from '~/pages/subscriptions/components/SubscribeSuccessScreen';

type SubscribeApiError = {
    data?: {
        errors?: string[];
    };
};

type UseSubscribeFlowProps = {
    plan: Plan;
};

const UseSubscribeFlow = ({ plan }: UseSubscribeFlowProps) => {

    const { navigate, params } = useAppNavigate();
    const { member } = useSpringBigStore();

    const [isPlanDetails, setIsPlanDetails] = useState<boolean>(true);
    const [isEmailVerification, setIsEmailVerification] = useState<boolean>(false);
    const [isCreditCardFormSubscribe, setIsCreditCardFormSubscribe] = useState<boolean>(false);
    const [isSubscribeSuccess, setIsSubscribeSuccess] = useState<boolean>(false);
    const [priceSummary, setPriceSummary] = useState<string>('');
    const [billingSchedule, setBillingSchedule] = useState<BillingSchedule>();
    const [apiError, setApiError] = useState<string[] | null>(null);

    const { mutate: subscribe, isLoading: isLoadingSubscribe, error: errorSubscribe } = useSubscribe();

    useEffect(() => {
        if (errorSubscribe) {
            setApiError((errorSubscribe as SubscribeApiError)?.data?.errors);
        }
    }, [errorSubscribe]);

    const onSubscribeCancel = (): void => {
        navigate(`w/${params.wallet_id}/subscriptions`);
    };

    const onSubscribeProceed = (): void => {
        setIsPlanDetails(false);
        if (member.consumer.email_validated) {
            setIsCreditCardFormSubscribe(true);
        }
        else {
            setIsEmailVerification(true);
        }
    };

    const onEmailVerificationCancel = (): void => {
        setIsEmailVerification(false);
        setIsPlanDetails(true);
    };

    const onEmailVerificationProceed = (): void => {
        setIsEmailVerification(false);
        setIsCreditCardFormSubscribe(true);
    };

    const onCancelCreditCardFormSubscribe = (): void => {
        navigate(`w/${params.wallet_id}/subscriptions`);
    };

    const onSubmitCreditCardFormSubscribe = (data: CreditCardDto): void => {
        const payload = {
            member_id: params.wallet_id,
            plan_id: billingSchedule === 'monthly' ? plan.id : plan.yearly_id,
            subscription_id: null,
            cc_number: data.number,
            cc_name: data.name,
            cc_cvv: data.cvv,
            cc_expiry: data.expiry,
            cc_zip: data.zip,
            billing_schedule: billingSchedule,
            sharing_link_id: null,
        };

        subscribe(payload, {
            onSuccess: () => {
                setIsCreditCardFormSubscribe(false);
                setIsSubscribeSuccess(true);
            },
        });
    };

    const onSubscribeSuccessProceed = (destination: Destinations): void => {
        navigate(`w/${params.wallet_id}/${destination}`);
    };

    const onUpdateBillingSchedule = (data: BillingSchedule): void => {
        setBillingSchedule(data);
        setPriceSummary(
            `$${ data === 'monthly' ? `${ plan.recurring_amount } a month` : `${ plan.yearly_recurring_amount } a year` }. Cancel at any time`
        );
    };

    return {
        isPlanDetails,
        isEmailVerification,
        isCreditCardFormSubscribe,
        isSubscribeSuccess,
        priceSummary,
        apiError,
        isLoadingSubscribe,
        onSubscribeCancel,
        onSubscribeProceed,
        onEmailVerificationCancel,
        onEmailVerificationProceed,
        onCancelCreditCardFormSubscribe,
        onSubmitCreditCardFormSubscribe,
        onSubscribeSuccessProceed,
        onUpdateBillingSchedule,
    };
};

export default UseSubscribeFlow;
